import { formatValue } from './widgetFormat.js';

/**
 * `slots` — how `LIST` and `PROFILE_CARD` describe one record (CT-5641).
 *
 * Both types publish the same thing: a record's fields, already sorted into
 * six named buckets by the author's binding. Not a flat `fields[]` — the bucket
 * *is* the instruction for how to draw the field, and it is what lets one
 * component render a vehicle, a customer and a repair order without knowing
 * anything about any of them.
 *
 * | Slot | What belongs in it | How it draws |
 * |---|---|---|
 * | `identity` | what the record *is* — plate, name, number | the title, strongest type |
 * | `status` | the state it is in | a badge |
 * | `signal` | something that wants attention — overdue, over budget | a badge, warning tone |
 * | `fact` | a plain attribute — model, tier, amount | a label/value pair |
 * | `context` | where it sits — branch, campaign | a label/value pair, muted |
 * | `source` | where it came from — system, import | a label/value pair, muted |
 *
 * The order here is the reading order, and it is the one thing a widget must
 * not take from the payload: a backend that happens to serialise `source`
 * first should not push the record's own name below it.
 */
export const SLOT_ORDER = Object.freeze([
  'identity',
  'status',
  'signal',
  'fact',
  'context',
  'source',
]);

/** Slots drawn as badges rather than as label/value pairs. */
export const BADGE_SLOTS = Object.freeze(['status', 'signal']);

/** Slots that describe the record's surroundings rather than the record. */
export const MUTED_SLOTS = Object.freeze(['context', 'source']);

/**
 * One slot's cells, formatted and ready to print.
 *
 * A cell with no value is dropped rather than printed empty: an absent field
 * on a record is not a field whose value is "—", and a profile card padded
 * with em dashes reads as a broken record.
 *
 * @param {object} slots A record's `slots` object.
 * @param {string} name A slot name from `SLOT_ORDER`.
 * @returns {{ key: string, label: string, text: string, value: * }[]}
 */
export const slotCells = (slots, name) =>
  (slots?.[name] ?? [])
    .filter((cell) => cell?.value !== null && cell?.value !== undefined && cell?.value !== '')
    .map((cell) => ({
      key: cell.key,
      label: cell.label ?? cell.key,
      text: formatValue(cell.value, cell.format),
      value: cell.value,
    }));

/** Every cell of every slot, in reading order, each tagged with its slot. */
export const allSlotCells = (slots) =>
  SLOT_ORDER.flatMap((name) => slotCells(slots, name).map((cell) => ({ ...cell, slot: name })));
